import React, { useState, useEffect } from 'react';
import DashboardTab from '../AICenter/DashboardTab';
import CallsTab from '../AICenter/CallsTab';
import AgentsTab from '../AICenter/AgentsTab';
import AnalyticsTab from '../AICenter/AnalyticsTab';
import LeadsTab from '../AICenter/LeadsTab';
import ConversationsTab from '../AICenter/ConversationsTab';
import PromptsTab from '../AICenter/PromptsTab';
import ToolsTab from '../AICenter/ToolsTab';
import SettingsTab from '../AICenter/SettingsTab';
import IntegrationsPanel from './IntegrationsPanel';

// Yonetim panelinden ("CRM Firmalari" > "AI Center") belirli bir subenin TUM AI
// modulunu yonetmek icin - client/src/pages/AICenter.js'in (tenant sayfasi)
// BIREBIR ayni mantigi, sadece admin_token + /api/admin/branches/:branchId/...
// path'lerine uyarlanmis hali. Backend tarafinda ai.js/callops.js/branches.js
// HIC DEGISTIRILMEDI - ayni router'lar "impersonation mount" ile ikinci kez
// admin path'inde calisiyor (bkz. server/middleware/adminImpersonateBranch.js).
//
// Çocuk bileşenlerden ConversationsTab/PromptsTab/ToolsTab/SettingsTab hiç
// değiştirilmedi (zaten prop tabanlı çalışıyorlardı). Dashboard/Calls/Agents/
// Analytics/Leads'e ise geriye-uyumlu opsiyonel apiBase/token prop'ları
// eklendi (bkz. ilgili dosyalar) - burada admin path/token ile besleniyor.
const AdminAICenterPanel = ({ branchId, branchName }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [companyPrompt, setCompanyPrompt] = useState('');
  const [operationMode, setOperationMode] = useState('online');
  const [demoWhatsappNumber, setDemoWhatsappNumber] = useState('');
  const [demoInstagramId, setDemoInstagramId] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [historyModal, setHistoryModal] = useState({ open: false, channel: 'all', items: [] });
  const [onboardingLink, setOnboardingLink] = useState('');
  const [onboardingSubmissions, setOnboardingSubmissions] = useState([]);
  const [submissionModal, setSubmissionModal] = useState({ open: false, item: null });
  const [webhookModal, setWebhookModal] = useState({ open: false, channel: null });
  const [toast, setToast] = useState(null);

  const token = localStorage.getItem('admin_token');
  const aiBase = `/api/admin/branches/${branchId}/ai`;
  const callopsBase = `/api/admin/branches/${branchId}/callops`;
  const branchBase = `/api/admin/branches/${branchId}/branch`;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => { fetchAllData(); }, [branchId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchSettings(), fetchConversations(), fetchOnboardingLink(), fetchOnboardingSubmissions()]);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${aiBase}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings(data);
      setCompanyPrompt(data.company_prompt || '');
      setOperationMode(data.operation_mode || 'online');
      setDemoWhatsappNumber(data.demo_whatsapp_number || '');
      setDemoInstagramId(data.demo_instagram_id || '');
    } catch (err) { console.error(err); }
  };

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${aiBase}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setConversations(data.data || []);
    } catch (err) { console.error(err); setConversations([]); }
  };

  const fetchOnboardingLink = async () => {
    try {
      const res = await fetch(`${branchBase}/${branchId}/onboarding-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setOnboardingLink(data.url || '');
    } catch (err) { console.error(err); }
  };

  const fetchOnboardingSubmissions = async () => {
    try {
      const res = await fetch(`${aiBase}/onboarding-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setOnboardingSubmissions(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); setOnboardingSubmissions([]); }
  };

  const copyOnboardingLink = () => {
    navigator.clipboard.writeText(onboardingLink);
    showToast('✅ Link kopyalandı!', 'success');
  };

  const applyOnboardingSubmission = async (id) => {
    if (!confirm('Bu yanıt onaylanıp uygulansın mı? Çalışma saatleri ve hizmetler güncellenecek, bir prompt taslağı oluşturulacak.')) return;
    setLoading(true);
    try {
      const res = await fetch(`${aiBase}/onboarding-submissions/${id}/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Uygulama hatası');
      setSubmissionModal({ open: false, item: null });
      await Promise.all([fetchOnboardingSubmissions(), fetchSettings()]);
      try {
        const draftRes = await fetch(`${aiBase}/prompts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const drafts = await draftRes.json();
        const draft = Array.isArray(drafts) ? drafts.find(p => p.id === data.prompt_id) : null;
        if (draft?.system_prompt) setCompanyPrompt(draft.system_prompt);
      } catch (draftErr) { console.error(draftErr); }
      showToast(`✅ Uygulandı! Prompt taslağı ${data.generated_via === 'llm' ? 'AI ile' : 'şablonla'} oluşturuldu — "İşletmenize Özel Eklemeler" alanına dolduruldu, gözden geçirip kaydedin.`, 'success');
    } catch (err) {
      console.error(err);
      showToast('❌ ' + err.message, 'error');
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      const payload = {
        is_enabled: settings.is_enabled,
        provider: settings.provider,
        model: settings.model,
        gemini_model: settings.gemini_model,
        confidence_threshold: settings.confidence_threshold,
        max_history: settings.max_history,
        language: settings.language,
        openai_api_key: settings.openai_api_key,
        gemini_api_key: settings.gemini_api_key
      };
      await fetch(`${aiBase}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      showToast('✅ Ayarlar kaydedildi!', 'success');
    } catch (err) { console.error(err); showToast('❌ Kaydetme hatası', 'error'); }
    setLoading(false);
  };

  const toggleTool = async (tool) => {
    try {
      await fetch(`${aiBase}/tools/${tool.name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: !tool.enabled })
      });
      setSettings({ ...settings, tools: settings.tools.map(t => t.name === tool.name ? { ...t, enabled: !t.enabled } : t) });
    } catch (err) { console.error(err); }
  };

  const saveCompanyPrompt = async () => {
    setLoading(true);
    try {
      await fetch(`${aiBase}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company_prompt: companyPrompt })
      });
      await fetchSettings();
      showToast('✅ İşletmeye özel ekleriniz kaydedildi!', 'success');
    } catch (err) { console.error(err); showToast('❌ Kaydetme hatası', 'error'); }
    setLoading(false);
  };

  // Eski (tek parça) modu bir sektor standardi + firma eki katmanina tasir.
  // system_prompt DB'de dokunulmadan kalir, revert-to-legacy onu geri kullanir.
  const migrateToLayered = async () => {
    if (!window.confirm('Bu şubeyi yeni sisteme (katmanlı moda) geçirmek istediğinize emin misiniz?\n\nEski promptu silinmez, "Eski Moda Dön" ile geri dönülebilir.')) return;
    setLoading(true);
    try {
      const res = await fetch(`${aiBase}/settings/migrate-to-layered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company_prompt: '' })
      });
      const data = await res.json();
      if (!res.ok) { showToast(`❌ ${data.error || 'Geçiş başarısız'}`, 'error'); return; }
      await fetchSettings();
      showToast('✅ Yeni sisteme geçildi!', 'success');
    } catch (err) { console.error(err); showToast('❌ Geçiş hatası', 'error'); }
    setLoading(false);
  };

  const revertToLegacy = async () => {
    if (!window.confirm('Bu şubeyi eski (tek parça) moda döndürmek istediğinize emin misiniz?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${aiBase}/settings/revert-to-legacy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { showToast(`❌ ${data.error || 'Geri dönüş başarısız'}`, 'error'); return; }
      await fetchSettings();
      showToast('✅ Eski moda dönüldü', 'success');
    } catch (err) { console.error(err); showToast('❌ Geri dönüş hatası', 'error'); }
    setLoading(false);
  };

  const saveOperationMode = async () => {
    if (operationMode === 'demo' && !demoWhatsappNumber.trim() && !demoInstagramId.trim()) {
      showToast('⚠️ Demo modda en az bir numara veya Instagram ID girin, aksi halde AI hiç kimseye cevap vermez', 'warning');
      return;
    }
    setLoading(true);
    try {
      await fetch(`${aiBase}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          operation_mode: operationMode,
          demo_whatsapp_number: demoWhatsappNumber.trim(),
          demo_instagram_id: demoInstagramId.trim()
        })
      });
      await fetchSettings();
      showToast(operationMode === 'demo' ? '✅ Demo modu aktif — sadece girdiğiniz numara/ID cevap alacak' : '✅ Online mod aktif — AI herkese cevap veriyor', 'success');
    } catch (err) { console.error(err); showToast('❌ Kaydetme hatası', 'error'); }
    setLoading(false);
  };

  const fetchPromptHistory = async (channel) => {
    try {
      const params = channel && channel !== 'all' ? `?channel=${channel}` : '';
      const res = await fetch(`${aiBase}/prompts/history${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setHistoryModal({ open: true, channel: channel || 'all', items: Array.isArray(data) ? data : [] });
    } catch (err) { console.error(err); showToast('❌ Geçmiş yüklenemedi', 'error'); }
  };

  const restorePromptHistory = async (item) => {
    if (!confirm('Bu sürüme dönülsün mü? Mevcut hali de geçmişe kaydedilecek.')) return;
    setLoading(true);
    try {
      await fetch(`${aiBase}/prompts/history/${item.id}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryModal({ open: false, channel: 'all', items: [] });
      await fetchSettings();
      showToast('✅ Eski sürüme dönüldü!', 'success');
    } catch (err) { console.error(err); showToast('❌ Geri alma hatası', 'error'); }
    setLoading(false);
  };

  const testPromptHandler = async () => {
    const composedPrompt = settings?.composed_prompt;
    if (!composedPrompt || !testMessage) return;
    setLoading(true);
    setTestResult(null);
    try {
      const res = await fetch(`${aiBase}/test-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: composedPrompt, message: testMessage, provider: settings?.provider, model: settings?.model })
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const openWebhookModal = (channel) => {
    const existing = settings?.webhooks?.find(w => w.channel === channel);
    setWebhookModal({ open: true, channel, edit: existing ? { ...existing } : { channel } });
  };

  const saveWebhook = async (webhookData) => {
    setLoading(true);
    try {
      await fetch(`${aiBase}/webhooks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...webhookData, channel: webhookModal.channel })
      });
      setWebhookModal({ open: false, channel: null });
      fetchSettings();
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const testWebhook = async (channel) => {
    setLoading(true);
    try {
      const res = await fetch(`${aiBase}/webhooks/${channel}/test`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      showToast(data.message || data.error || '✅ Test gönderildi', data.error ? 'error' : 'success');
      fetchSettings();
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchConversationDetail = async (id) => {
    const res = await fetch(`${aiBase}/conversations/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    setSelectedConversation(data);
  };

  const handoffConversation = async (id, response) => {
    try {
      await fetch(`${aiBase}/conversations/${id}/handoff`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ staff_response: response })
      });
      fetchConversations();
      setSelectedConversation(null);
    } catch (err) { console.error(err); }
  };

  const respondConversation = async (id, response) => {
    try {
      await fetch(`${aiBase}/conversations/${id}/respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ response })
      });
      fetchConversationDetail(id);
    } catch (err) { console.error(err); }
  };

  const resolveConversation = async (id) => {
    try {
      await fetch(`${aiBase}/conversations/${id}/resolve`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      fetchConversations();
      setSelectedConversation(null);
    } catch (err) { console.error(err); }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleString('tr-TR') : '-';

  const getStatusBadge = (status) => {
    const s = { active: 'badge badge-primary', auto_resolved: 'badge badge-success', handed_to_human: 'badge badge-warning', pending: 'badge badge-gray' };
    const l = { active: 'Aktif', auto_resolved: 'Çözüldü', handed_to_human: 'Personelde', pending: 'Bekliyor' };
    return <span className={s[status] || 'badge badge-gray'}>{l[status] || status}</span>;
  };

  const tabs = [
    { id: 'dashboard', label: 'Genel Bakış', icon: '📊', group: 'main' },
    { id: 'calls', label: 'Aramalar', icon: '📞', group: 'main' },
    { id: 'agents', label: 'AI Agentlar', icon: '🤖', group: 'main' },
    { id: 'analytics', label: 'Analitik', icon: '📈', group: 'main' },
    { id: 'leads', label: 'Leadler', icon: '🎯', group: 'main' },
    { id: 'conversations', label: 'Konuşmalar', icon: '💬', group: 'main' },
    { id: 'prompts', label: 'Prompt Yönetimi', icon: '✨', group: 'config' },
    { id: 'tools', label: 'Araçlar & Entegrasyon', icon: '🔧', group: 'config' },
    { id: 'settings', label: 'Ayarlar', icon: '⚙️', group: 'config' },
    { id: 'integrations', label: 'Entegrasyonlar', icon: '🔌', group: 'config' }
  ];

  if (loading && !settings) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  const mainTabs = tabs.filter(t => t.group === 'main');
  const configTabs = tabs.filter(t => t.group === 'config');

  return (
    <div className="callops-center admin-ai-center">
      {toast && (
        <div className={`ai-toast ${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">🚀 {branchName || 'Şube'} — AI Center</h1>
          <p className="page-subtitle">Yönetim panelinden tam AI yönetimi</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchAllData} disabled={loading}>
          {loading ? '⏳' : '🔄'} Yenile
        </button>
      </div>

      <div className="callops-layout">
        <div className="callops-sidebar">
          <div className="sidebar-group-label">Operasyon</div>
          {mainTabs.map(tab => (
            <button key={tab.id} className={`callops-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <span className="tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
          <div className="sidebar-divider" />
          <div className="sidebar-group-label">Konfigürasyon</div>
          {configTabs.map(tab => (
            <button key={tab.id} className={`callops-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <span className="tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="callops-content">
          {activeTab === 'dashboard' && <DashboardTab apiBase={callopsBase} token={token} />}

          {activeTab === 'calls' && <CallsTab apiBase={callopsBase} token={token} />}

          {activeTab === 'agents' && <AgentsTab apiBase={callopsBase} token={token} />}

          {activeTab === 'analytics' && <AnalyticsTab apiBase={callopsBase} token={token} />}

          {activeTab === 'leads' && <LeadsTab apiBase={callopsBase} token={token} />}

          {activeTab === 'conversations' && (
            <ConversationsTab
              conversations={conversations}
              selectedConversation={selectedConversation}
              fetchConversationDetail={fetchConversationDetail}
              handoffConversation={handoffConversation}
              respondConversation={respondConversation}
              resolveConversation={resolveConversation}
              getStatusBadge={getStatusBadge}
              formatDate={formatDate}
              onRefresh={fetchConversations}
            />
          )}

          {activeTab === 'prompts' && (
            <PromptsTab
              testMessage={testMessage}
              setTestMessage={setTestMessage}
              testResult={testResult}
              testPromptHandler={testPromptHandler}
              saveSettings={saveSettings}
              operationMode={operationMode}
              setOperationMode={setOperationMode}
              demoWhatsappNumber={demoWhatsappNumber}
              setDemoWhatsappNumber={setDemoWhatsappNumber}
              demoInstagramId={demoInstagramId}
              setDemoInstagramId={setDemoInstagramId}
              saveOperationMode={saveOperationMode}
              companyPrompt={companyPrompt}
              setCompanyPrompt={setCompanyPrompt}
              saveCompanyPrompt={saveCompanyPrompt}
              promptMode={settings?.prompt_mode}
              systemPrompt={settings?.system_prompt}
              onMigrateToLayered={migrateToLayered}
              onRevertToLegacy={revertToLegacy}
              industryStandardPrompt={settings?.industry_standard_prompt}
              industryStandardVersion={settings?.industry_standard_version}
              industryStandardAvailable={settings?.industry_standard_available}
              composedPrompt={settings?.composed_prompt}
              composedLength={settings?.composed_length}
              unresolvedTokens={settings?.unresolved_tokens}
              missingStandard={settings?.missing_standard}
              loading={loading}
              onOpenHistory={fetchPromptHistory}
              onboardingLink={onboardingLink}
              copyOnboardingLink={copyOnboardingLink}
              onboardingSubmissions={onboardingSubmissions}
              onReviewSubmission={(item) => setSubmissionModal({ open: true, item })}
            />
          )}

          {activeTab === 'tools' && settings && (
            <ToolsTab
              settings={settings}
              toggleTool={toggleTool}
              webhookModal={webhookModal}
              openWebhookModal={openWebhookModal}
              setWebhookModal={setWebhookModal}
              saveWebhook={saveWebhook}
              testWebhook={testWebhook}
              formatDate={formatDate}
              loading={loading}
            />
          )}

          {activeTab === 'settings' && settings && (
            <SettingsTab
              settings={settings}
              setSettings={setSettings}
              saveSettings={saveSettings}
              loading={loading}
            />
          )}

          {activeTab === 'integrations' && (
            <IntegrationsPanel branchId={branchId} showToast={showToast} />
          )}
        </div>
      </div>

      {historyModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>🕐 Prompt Geçmişi {historyModal.channel !== 'all' ? `— ${historyModal.channel}` : ''}</h2>
              <button className="modal-close" onClick={() => setHistoryModal({ open: false, channel: 'all', items: [] })}>×</button>
            </div>
            <div className="modal-body">
              {historyModal.items.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)' }}>Bu prompt için henüz kaydedilmiş bir geçmiş yok — yalnızca bu özellik eklendikten sonra yapılan kayıtlar burada görünür.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {historyModal.items.map(item => (
                    <div key={item.id} className="pt-custom-card" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatDate ? formatDate(item.created_at) : new Date(item.created_at).toLocaleString('tr-TR')} · {item.source_type}{item.channel ? ` · ${item.channel}` : ''}</span>
                        <button className="btn btn-secondary btn-sm" onClick={() => restorePromptHistory(item)} disabled={loading}>Bu sürüme dön</button>
                      </div>
                      <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-secondary)', maxHeight: 80, overflow: 'hidden' }}>
                        {item.system_prompt.substring(0, 200)}{item.system_prompt.length > 200 ? '...' : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setHistoryModal({ open: false, channel: 'all', items: [] })}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {submissionModal.open && submissionModal.item && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>📋 {submissionModal.item.business_name}</h2>
              <button className="modal-close" onClick={() => setSubmissionModal({ open: false, item: null })}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">İletişim</label>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {submissionModal.item.address || '-'} · {submissionModal.item.phone || '-'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Çalışma Saatleri</label>
                <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                  {Object.entries(submissionModal.item.working_hours || {}).map(([day, v]) => (
                    <div key={day}>{day}: {v?.active ? `${v.open}-${v.close}` : 'Kapalı'}</div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Hizmetler</label>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {(submissionModal.item.services || []).map((s, i) => (
                    <div key={i}>{s.name} — {s.price}₺ ({s.duration_min} dk)</div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Ton</label>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{submissionModal.item.tone || '-'}</div>
              </div>
              <div className="form-group">
                <label className="form-label">Yetkiler</label>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[
                    ['Randevu alabilir', submissionModal.item.can_book_appointments],
                    ['Ön görüşme alabilir', submissionModal.item.can_take_pre_consultation],
                    ['Fiyat paylaşabilir', submissionModal.item.can_share_prices],
                    ['Sağlık/bakım tavsiyesi verebilir', submissionModal.item.can_give_health_advice]
                  ].map(([label, val], i) => (
                    <div key={i}>{val === 1 || val === true ? '✅' : val === 0 || val === false ? '❌' : '➖'} {label}</div>
                  ))}
                </div>
              </div>
              {submissionModal.item.special_notes && (
                <div className="form-group">
                  <label className="form-label">Özel Notlar</label>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{submissionModal.item.special_notes}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSubmissionModal({ open: false, item: null })}>Kapat</button>
              {submissionModal.item.status === 'pending' && (
                <button className="btn btn-primary" onClick={() => applyOnboardingSubmission(submissionModal.item.id)} disabled={loading}>
                  ✅ Onayla ve Uygula
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {webhookModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{webhookModal.channel} Webhook</h2>
              <button className="modal-close" onClick={() => setWebhookModal({ open: false, channel: null })}>×</button>
            </div>
            <div className="modal-body">
              {webhookModal.channel === 'instagram' ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Meta Callback URL (Meta App'inize bunu girin)</label>
                    <input className="input" readOnly value={`${window.location.origin}/webhook/instagram/${branchId}`} onFocus={(e) => e.target.select()} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Verify Token (Meta App'te aynısını girin)</label>
                    <input className="input" placeholder="kendi belirlediğiniz doğrulama kelimesi" value={webhookModal.edit?.verify_token || ''} onChange={(e) => setWebhookModal({ ...webhookModal, edit: { ...webhookModal.edit, verify_token: e.target.value } })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Access Token</label>
                    <input className="input" type="password" placeholder="Meta Page Access Token" value={webhookModal.edit?.access_token || ''} onChange={(e) => setWebhookModal({ ...webhookModal, edit: { ...webhookModal.edit, access_token: e.target.value } })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Instagram Business Account ID</label>
                    <input className="input" placeholder="17841..." value={webhookModal.edit?.phone_number_id || ''} onChange={(e) => setWebhookModal({ ...webhookModal, edit: { ...webhookModal.edit, phone_number_id: e.target.value } })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">App Secret</label>
                    <input className="input" type="password" placeholder="Meta App Secret" value={webhookModal.edit?.app_secret || ''} onChange={(e) => setWebhookModal({ ...webhookModal, edit: { ...webhookModal.edit, app_secret: e.target.value } })} />
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label className="form-label">Webhook URL</label>
                  <input className="input" placeholder="https://..." value={webhookModal.edit?.webhook_url || ''} onChange={(e) => setWebhookModal({ ...webhookModal, edit: { ...webhookModal.edit, webhook_url: e.target.value } })} />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setWebhookModal({ open: false, channel: null })}>İptal</button>
              <button className="btn btn-primary" onClick={() => saveWebhook(webhookModal.edit)} disabled={loading}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-ai-center.callops-center { min-height: auto; background: transparent; }

        .admin-ai-center .callops-layout { display: flex; gap: 24px; padding: 0; }

        .admin-ai-center .callops-sidebar {
          width: 220px; flex-shrink: 0;
          background: rgba(8,8,14,0.96);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px; padding: 16px 10px;
          display: flex; flex-direction: column; gap: 2px;
          backdrop-filter: blur(24px);
          height: fit-content;
        }

        .admin-ai-center .sidebar-group-label {
          font-size: 10px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.08em; color: #374151;
          padding: 8px 12px 4px;
        }

        .admin-ai-center .sidebar-divider { height: 1px; background: rgba(255,255,255,0.05); margin: 8px 4px; }

        .admin-ai-center .callops-tab {
          display: flex; align-items: center; gap: 10px;
          padding: 11px 14px; border: none;
          background: transparent; color: rgba(255,255,255,0.4);
          font-size: 13px; font-weight: 500; text-align: left;
          cursor: pointer; border-radius: 10px; transition: all 0.18s;
          width: 100%;
        }

        .admin-ai-center .tab-icon { font-size: 16px; flex-shrink: 0; }

        .admin-ai-center .callops-tab:hover { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.8); }

        .admin-ai-center .callops-tab.active {
          background: rgba(96,165,250,0.1);
          border: 1px solid rgba(96,165,250,0.2);
          color: #60A5FA;
        }

        .admin-ai-center .callops-content { flex: 1; min-width: 0; }

        .admin-ai-center .page-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 0 0 20px;
        }

        .admin-ai-center .page-title { font-size: 22px; font-weight: 800; color: #FFFFFF; margin: 0; }
        .admin-ai-center .page-subtitle { color: #6B7280; margin: 4px 0 0 0; font-size: 13px; }

        .admin-ai-center .ai-toast {
          position: fixed; bottom: 24px; right: 24px; z-index: 9999;
          padding: 14px 20px; border-radius: 14px; font-size: 14px; font-weight: 600;
          backdrop-filter: blur(20px); animation: adminToastIn 0.3s ease;
        }
        .admin-ai-center .ai-toast.success { background: rgba(52,211,153,0.15); border: 1px solid rgba(52,211,153,0.3); color: #34D399; }
        .admin-ai-center .ai-toast.error { background: rgba(248,113,113,0.15); border: 1px solid rgba(248,113,113,0.3); color: #F87171; }
        .admin-ai-center .ai-toast.warning { background: rgba(251,191,36,0.15); border: 1px solid rgba(251,191,36,0.3); color: #FBBF24; }
        @keyframes adminToastIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }

        .admin-ai-center .modal-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.75); backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; z-index: 1000;
        }

        .admin-ai-center .modal {
          background: rgba(17,17,24,0.95);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px; width: 90%; max-width: 600px;
          max-height: 90vh; overflow: hidden; display: flex; flex-direction: column;
          box-shadow: 0 24px 48px rgba(0,0,0,0.6); backdrop-filter: blur(40px);
        }

        .admin-ai-center .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          display: flex; justify-content: space-between; align-items: center;
        }

        .admin-ai-center .modal-header h2 { margin: 0; font-size: 20px; color: #FFFFFF; }

        .admin-ai-center .modal-close {
          background: none; border: none; font-size: 28px; cursor: pointer;
          color: #6B7280; transition: color 0.15s;
        }
        .admin-ai-center .modal-close:hover { color: #EF4444; }

        .admin-ai-center .modal-body { padding: 24px; overflow-y: auto; }

        .admin-ai-center .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid rgba(255,255,255,0.06);
          display: flex; gap: 12px; justify-content: flex-end;
        }

        .admin-ai-center .text-muted { color: #6B7280; font-size: 14px; }
        .admin-ai-center .font-mono { font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace; }
      `}</style>
    </div>
  );
};

export default AdminAICenterPanel;
